/**
 * Clinic backend domain calls for FE-2 (all verified against cms-backend
 * source @ f7c934b). Views are transcriptions of the backend's TS views.
 */

import { apiRequest } from '../api/http';
import { buildQueryParam, type PagedResult, type QuerySpecInput } from '../api/querySpec';
import type { ClinicView } from './api';

/* ------------------------------- clinic ---------------------------------- */

export interface ClinicProfilePatch {
  displayName?: string;
  address?: string;
  contactPhone?: string;
  contactEmail?: string;
  operatingContext?: string;
  /** '' clears; the server canonicalises (400 CLINIC_WEBSITE_INVALID). */
  website?: string;
  /** '' clears; an @ or a profile URL is accepted and stripped (400 CLINIC_INSTAGRAM_INVALID). */
  instagram?: string;
}

/**
 * PATCH /v1/clinic/settings — every member optional; OMIT a member to leave
 * it unchanged (ZOY-287: never send null for defaultBufferMinutes).
 */
export interface ClinicSettingsPatch {
  calendarSystem?: 'jalali' | 'gregorian';
  currencyCode?: string;
  defaultBufferMinutes?: number; // 0–240
  /** ZOY-252: 0–480 minutes each way. */
  defaultTravelAllowanceMinutes?: number;
  timezone?: string;
  /** tasks#138: sent only when it changed; never null. */
  practitionerScheduleSms?: boolean;
}

export const patchClinicProfile = (body: ClinicProfilePatch) =>
  apiRequest<ClinicView>({ base: 'clinic', method: 'PATCH', path: '/v1/clinic/profile', body });

export const patchClinicSettings = (body: ClinicSettingsPatch) =>
  apiRequest<ClinicView>({ base: 'clinic', method: 'PATCH', path: '/v1/clinic/settings', body });

/* ------------------------------ personnel --------------------------------- */

export interface PractitionerFields {
  specialty?: string;
  licenseNumber?: string;
  experience?: string;
  bio?: string;
  qualifiedToAssess?: boolean;
  /** null clears a stored value (the DTO's @IsOptional admits it; the service writes it). */
  bufferMinutes?: number | null;
  maxSessionsPerDay?: number | null;
  maxSessionsPerWeek?: number | null;
}

export interface PersonnelView {
  uid: string;
  /**
   * ZOY-363/423: the display NAME every roster/filter/calendar consumer
   * needs, resolved from Auth BY THE BACKEND (batched per page — never a
   * per-row lookup here). Null when Auth holds no name or was momentarily
   * unavailable; uid stays the machine identifier.
   */
  displayName: string | null;
  jobTitle: string | null;
  workPhone: string | null;
  workEmail: string | null;
  practitioner: (PractitionerFields & Record<string, unknown>) | null;
}

/** GET /v1/personnel — query contract (allow-listed: uid, jobTitle). */
export const listPersonnel = (spec: QuerySpecInput) =>
  apiRequest<PagedResult<PersonnelView>>({
    base: 'clinic',
    path: '/v1/personnel',
    query: { query: buildQueryParam(spec) },
  });

export const fetchPersonnel = (uid: string) =>
  apiRequest<PersonnelView>({ base: 'clinic', path: `/v1/personnel/${encodeURIComponent(uid)}` });

export const upsertPersonnel = (
  uid: string,
  body: {
    jobTitle?: string;
    workPhone?: string;
    workEmail?: string;
    practitioner?: PractitionerFields;
  },
) =>
  apiRequest<PersonnelView>({
    base: 'clinic',
    method: 'PUT',
    path: `/v1/personnel/${encodeURIComponent(uid)}`,
    body,
  });

/* -------------------------------- rooms ----------------------------------- */

export interface RoomView {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  roomType: string | null;
  disabled: boolean;
  bufferMinutes: number | null;
}

export interface RoomBody {
  name?: string;
  code?: string;
  description?: string;
  roomType?: string;
  /** null clears a stored buffer (the DTO's @IsOptional admits it; the service writes it). */
  bufferMinutes?: number | null;
}

export const listRooms = (spec: QuerySpecInput) =>
  apiRequest<PagedResult<RoomView>>({
    base: 'clinic',
    path: '/v1/rooms',
    query: { query: buildQueryParam(spec) },
  });

export const createRoom = (body: RoomBody & { name: string }) =>
  apiRequest<RoomView>({ base: 'clinic', method: 'POST', path: '/v1/rooms', body });

export const patchRoom = (id: string, body: RoomBody) =>
  apiRequest<RoomView>({ base: 'clinic', method: 'PATCH', path: `/v1/rooms/${id}`, body });

export const setRoomDisabled = (id: string, disabled: boolean) =>
  apiRequest<RoomView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/rooms/${id}/${disabled ? 'disable' : 'enable'}`,
  });

/* ------------------------------- services --------------------------------- */

/** LEGACY physical designation, derived by the backend from `modes` (ZOY-237). Read-only here. */
export type ServiceLocation = 'in_clinic' | 'off_site' | 'both';
/** ZOY-237: the enabled delivery modes of a service or a working-hours window — the source of truth. */
export const DELIVERY_MODES = ['in_clinic', 'off_site', 'online'] as const;
export type DeliveryMode = (typeof DELIVERY_MODES)[number];
/** Older reads carry only the legacy designation; the set it always meant. */
export function modesOf(view: { modes?: readonly string[]; location?: string }): DeliveryMode[] {
  if (Array.isArray(view.modes) && view.modes.length > 0)
    return DELIVERY_MODES.filter((m) => view.modes!.includes(m));
  if (view.location === 'both') return ['in_clinic', 'off_site'];
  if (view.location === 'off_site') return ['off_site'];
  return ['in_clinic'];
}

/**
 * ZOY-542: the platform workflow vocabulary — the standardized clinical
 * meaning a service declares ONCE at creation. Slugs are the wire contract;
 * Persian labels are UI copy only.
 */
export const WORKFLOW_TYPES = [
  'assessment',
  'speech_therapy_session',
  'occupational_therapy_session',
  'consultation',
  'virtual_reality_session',
  'neurotherapy_session',
  /** ZOY-685: a therapist-led GROUP session — one occurrence, several participants. */
  'group_session',
] as const;
export type WorkflowType = (typeof WORKFLOW_TYPES)[number];

export interface ServiceView {
  id: string;
  name: string;
  description: string | null;
  /** ZOY-542: immutable clinical meaning; null = unclassified legacy service. */
  workflowType: string | null;
  source: 'platform' | 'clinic';
  active: boolean;
  disabled: boolean;
  price: number | null;
  location: ServiceLocation;
  /** ZOY-237: never empty on current reads. */
  modes?: DeliveryMode[];
  durationMinutes: number;
  /** ZOY-685: default participant capacity of a group occurrence; null = stated per occurrence. */
  defaultGroupCapacity?: number | null;
}

export const listServices = (spec: QuerySpecInput) =>
  apiRequest<PagedResult<ServiceView>>({
    base: 'clinic',
    path: '/v1/catalog/services',
    query: { query: buildQueryParam(spec) },
  });

export const createService = (body: {
  name: string;
  /** ZOY-542: REQUIRED — the backend refuses creation without it. */
  workflowType: WorkflowType;
  description?: string;
  price?: number;
  /** ZOY-237: the set wins over the legacy `location`. */
  modes?: DeliveryMode[];
  durationMinutes?: number;
  defaultGroupCapacity?: number;
}) =>
  apiRequest<ServiceView>({ base: 'clinic', method: 'POST', path: '/v1/catalog/services', body });

export const patchServiceSettings = (
  id: string,
  body: {
    price?: number | null;
    modes?: DeliveryMode[];
    durationMinutes?: number;
    /** null clears the default; the capacity is then stated on every occurrence. */
    defaultGroupCapacity?: number | null;
  },
) =>
  apiRequest<ServiceView>({
    base: 'clinic',
    method: 'PATCH',
    path: `/v1/catalog/services/${id}/settings`,
    body,
  });

export const setServiceDisabled = (id: string, disabled: boolean) =>
  apiRequest<ServiceView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/catalog/services/${id}/${disabled ? 'disable' : 'enable'}`,
  });

/* ----------------------------- capabilities -------------------------------- */

export interface PractitionerCapabilityView {
  id: string;
  uid: string;
  serviceId: string;
  serviceName: string;
  matchScore: number | null;
  isPreferred: boolean;
  eligible: boolean;
}

export interface RoomCapabilityView {
  id: string;
  roomId: string;
  roomName: string;
  serviceId: string;
  serviceName: string;
  eligible: boolean;
}

export const listPractitionerCapabilities = (spec: QuerySpecInput) =>
  apiRequest<PagedResult<PractitionerCapabilityView>>({
    base: 'clinic',
    path: '/v1/capabilities/practitioners',
    query: { query: buildQueryParam(spec) },
  });

/** Every practitioner↔service link of the clinic, all pages (capped at 20 × 100 for safety). */
export async function listAllPractitionerCapabilities(): Promise<PractitionerCapabilityView[]> {
  const out: PractitionerCapabilityView[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < 20; i += 1) {
    const page = await listPractitionerCapabilities({
      limit: 100,
      sort: [{ field: 'uid', dir: 'asc' }],
      cursor,
    });
    out.push(...page.items);
    if (!page.nextCursor) break;
    cursor = page.nextCursor;
  }
  return out;
}

export const addPractitionerCapability = (body: {
  uid: string;
  serviceId: string;
  matchScore?: number;
  isPreferred?: boolean;
}) =>
  apiRequest<PractitionerCapabilityView>({
    base: 'clinic',
    method: 'POST',
    path: '/v1/capabilities/practitioners',
    body,
  });

export const removePractitionerCapability = (id: string) =>
  apiRequest<void>({
    base: 'clinic',
    method: 'DELETE',
    path: `/v1/capabilities/practitioners/${id}`,
  });

/**
 * Verified: a bounded, UNpaginated per-clinic read — the route takes no
 * query and returns every room↔service link; callers narrow client-side.
 */
export const listRoomCapabilities = () =>
  apiRequest<{ items: RoomCapabilityView[] }>({
    base: 'clinic',
    path: '/v1/capabilities/rooms',
  });

export const addRoomCapability = (body: { roomId: string; serviceId: string }) =>
  apiRequest<RoomCapabilityView>({
    base: 'clinic',
    method: 'POST',
    path: '/v1/capabilities/rooms',
    body,
  });

export const removeRoomCapability = (id: string) =>
  apiRequest<void>({ base: 'clinic', method: 'DELETE', path: `/v1/capabilities/rooms/${id}` });

/* ----------------------------- availability -------------------------------- */

export interface WorkingHoursView {
  id: string;
  /** ISO day of week: 1 = Monday … 7 = Sunday (verified: availability/dto.ts). */
  dayOfWeek: number;
  startMinute: number;
  endMinute: number;
  /** LEGACY physical designation (practitioner windows), derived from `modes`. */
  location?: string;
  /** ZOY-237: the modes the practitioner works in during this window. */
  modes?: DeliveryMode[];
}

export interface AbsenceView {
  id: string;
  startsAt: string;
  endsAt: string;
  reason: string | null;
}

export const fetchRoomAvailability = (roomId: string) =>
  apiRequest<{ roomId: string; workingHours: WorkingHoursView[]; blockouts: AbsenceView[] }>({
    base: 'clinic',
    path: `/v1/availability/rooms/${roomId}`,
  });

export const fetchPractitionerAvailability = (uid: string) =>
  apiRequest<{ uid: string; workingHours: WorkingHoursView[]; timeOff: AbsenceView[] }>({
    base: 'clinic',
    path: `/v1/availability/practitioners/${encodeURIComponent(uid)}`,
  });

export const addWorkingHours = (
  uid: string,
  body: { dayOfWeek: number; startMinute: number; endMinute: number; modes: DeliveryMode[] },
) =>
  apiRequest<WorkingHoursView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/availability/practitioners/${encodeURIComponent(uid)}/working-hours`,
    body,
  });

export const deleteWorkingHours = (id: string) =>
  apiRequest<void>({
    base: 'clinic',
    method: 'DELETE',
    path: `/v1/availability/practitioners/working-hours/${id}`,
  });

export const addTimeOff = (
  uid: string,
  body: { startsAt: string; endsAt: string; reason?: string },
) =>
  apiRequest<AbsenceView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/availability/practitioners/${encodeURIComponent(uid)}/time-off`,
    body,
  });

export const deleteTimeOff = (id: string) =>
  apiRequest<void>({
    base: 'clinic',
    method: 'DELETE',
    path: `/v1/availability/practitioners/time-off/${id}`,
  });

/*
 * Room hours and blockouts (ZOY-636) — the same availability routes for a
 * room, under clinic.availability.manage (NOT clinic.rooms.manage). A room
 * with no configured hours is open by hours; configured hours close it
 * outside them.
 */
export const addRoomWorkingHours = (
  roomId: string,
  body: { dayOfWeek: number; startMinute: number; endMinute: number },
) =>
  apiRequest<WorkingHoursView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/availability/rooms/${roomId}/working-hours`,
    body,
  });

export const deleteRoomWorkingHours = (id: string) =>
  apiRequest<void>({
    base: 'clinic',
    method: 'DELETE',
    path: `/v1/availability/rooms/working-hours/${id}`,
  });

export const addRoomBlockout = (
  roomId: string,
  body: { startsAt: string; endsAt: string; reason?: string },
) =>
  apiRequest<AbsenceView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/availability/rooms/${roomId}/blockouts`,
    body,
  });

export const deleteRoomBlockout = (id: string) =>
  apiRequest<void>({
    base: 'clinic',
    method: 'DELETE',
    path: `/v1/availability/rooms/blockouts/${id}`,
  });

/* --------------------------- clinical catalogue ----------------------------- */

/**
 * Clinical catalogues (Intake redesign, ZOY-570/571/572 → ZOY-574). Three
 * lists under one governance shape: history items and complaints take
 * clinic-side additions; the medication master list is PLATFORM-ONLY —
 * clinics may disable/enable a row but never add one (POST is 404).
 */
export type ClinicalKind = 'medications' | 'history-items' | 'complaints';

export interface ClinicalItemView {
  id: string;
  name: string;
  description?: string | null;
  /** Medication only. */
  genericName?: string | null;
  /** History item / complaint: the drill-down grouping; medication: optional. */
  category?: string | null;
  /** History item only: which intake section; null = unclassified legacy row. */
  section?: string | null;
  /** The section's Other row — its selection carries the meaning as free text. */
  isOther: boolean;
  source: 'platform' | 'clinic';
  /** Platform archive state — an archived row still renders where already referenced. */
  active: boolean;
  /** Clinic-local disable state. */
  disabled: boolean;
}

/** Offer for NEW selection only what is live for this clinic. */
export const isSelectable = (i: Pick<ClinicalItemView, 'active' | 'disabled'>): boolean =>
  i.active && !i.disabled;

const clinicalPath = (kind: ClinicalKind) => `/v1/catalog/${kind}`;

export const listClinicalItems = (kind: ClinicalKind, spec: QuerySpecInput) =>
  apiRequest<PagedResult<ClinicalItemView>>({
    base: 'clinic',
    path: clinicalPath(kind),
    query: { query: buildQueryParam(spec) },
  });

/** ZOY-572: the medication picker's drill-down entry point — active rows this clinic can see. */
export const listMedicationCategories = () =>
  apiRequest<{ items: { category: string; count: number }[] }>({
    base: 'clinic',
    path: '/v1/catalog/medications/categories',
  });

/**
 * Clinic-side additions. History items need `category` AND `section`
 * (ZOY-570); complaints need `category` (ZOY-571). There is deliberately no
 * medication variant: the master list is platform-managed.
 */
export type CreateClinicalItemBody =
  | { kind: 'history-items'; name: string; category: string; section: string }
  | { kind: 'complaints'; name: string; category: string; description?: string };

export const createClinicalItem = ({ kind, ...body }: CreateClinicalItemBody) =>
  apiRequest<ClinicalItemView>({ base: 'clinic', method: 'POST', path: clinicalPath(kind), body });

export const setClinicalItemDisabled = (kind: ClinicalKind, id: string, disabled: boolean) =>
  apiRequest<ClinicalItemView>({
    base: 'clinic',
    method: 'POST',
    path: `${clinicalPath(kind)}/${id}/${disabled ? 'disable' : 'enable'}`,
  });
